import { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet';
import styles from '../styles/CubeTeg.module.css';

const FACES = ['front', 'back', 'right', 'left', 'top', 'bottom'];
const IDLE = { x: 0.06, y: 0.12 };

// one cubeteg, no accounts, no leaderboard, no store — drag it and it spins
function CubeTeg() {
    const stageRef = useRef(null);
    const cubeRef = useRef(null);
    const angleRef = useRef({ x: -18, y: 28 });
    const velocityRef = useRef({ x: 0, y: 0 });
    const lastPosRef = useRef({ x: 0, y: 0 });
    const draggingRef = useRef(false);
    const idleRef = useRef({ ...IDLE });
    const idleTargetRef = useRef({ ...IDLE });
    const [spun, setSpun] = useState(false);

    useEffect(() => {
        const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        let raf;
        const tick = () => {
            if (!draggingRef.current) {
                const idle = idleRef.current;
                const target = idleTargetRef.current;
                idle.x += (target.x - idle.x) * 0.02;
                idle.y += (target.y - idle.y) * 0.02;

                const angle = angleRef.current;
                const velocity = velocityRef.current;
                // every axis turns freely, in either direction, forever — the
                // only limit is keeping the numbers inside float precision
                angle.x += idle.x + velocity.x;
                angle.y += idle.y + velocity.y;
                if (Math.abs(angle.x) > 36000) angle.x %= 360;
                if (Math.abs(angle.y) > 36000) angle.y %= 360;
                velocity.x *= 0.98;
                velocity.y *= 0.98;

                if (cubeRef.current) {
                    cubeRef.current.style.transform =
                        `rotateX(${angle.x}deg) rotateY(${angle.y}deg)`;
                }
            }
            raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);

        const drift = reduced ? null : setInterval(() => {
            idleTargetRef.current = {
                x: (Math.random() - 0.5) * 0.3,
                y: (Math.random() - 0.5) * 0.3,
            };
        }, 3000);

        return () => {
            cancelAnimationFrame(raf);
            if (drift) clearInterval(drift);
        };
    }, []);

    const handlePointerDown = (e) => {
        if (e.button !== undefined && e.button !== 0) return;
        // capture so the spin survives leaving the stage — not every pointer
        // can be captured (synthetic/already-released ones), hence the guard
        try { e.currentTarget.setPointerCapture?.(e.pointerId); } catch { /* ignore */ }
        draggingRef.current = true;
        lastPosRef.current = { x: e.clientX, y: e.clientY };
        velocityRef.current = { x: 0, y: 0 };
    };

    const handlePointerMove = (e) => {
        if (!draggingRef.current) return;

        const last = lastPosRef.current;
        const dx = e.clientX - last.x;
        const dy = e.clientY - last.y;
        if (!dx && !dy) return;

        const angle = angleRef.current;
        const flip = Math.cos((angle.x * Math.PI) / 180) >= 0 ? 1 : -1;

        angle.x -= dy * 0.5;
        angle.y += dx * 0.5 * flip;
        velocityRef.current = { x: -dy * 0.5, y: dx * 0.5 * flip };
        lastPosRef.current = { x: e.clientX, y: e.clientY };

        if (cubeRef.current) {
            cubeRef.current.style.transform =
                `rotateX(${angle.x}deg) rotateY(${angle.y}deg)`;
        }
        if (!spun) setSpun(true);
    };

    const handlePointerUp = (e) => {
        draggingRef.current = false;
        try { e.currentTarget.releasePointerCapture?.(e.pointerId); } catch { /* ignore */ }
    };

    return (
        <div className={styles.page}>
            <Helmet>
                <title>ojee / CubeTeg</title>
                <meta name="description" content="One cubeteg. Spin it." />
                <meta name="robots" content="noindex, nofollow" />
                <link rel="canonical" href="https://ojee.net/cubeteg" />
            </Helmet>

            <header className={styles.head}>
                <span className={styles.kicker}>ojee.net / game</span>
                <h1 className={styles.title}>cubeteg</h1>
            </header>

            <div
                ref={stageRef}
                className={styles.stage}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                onPointerCancel={handlePointerUp}
            >
                <div className={styles.scene}>
                    <div
                        ref={cubeRef}
                        className={styles.cube}
                        style={{ transform: `rotateX(${angleRef.current.x}deg) rotateY(${angleRef.current.y}deg)` }}
                    >
                        {FACES.map(face => (
                            <div key={face} className={`${styles.face} ${styles[face]}`}>
                                <img src="res/teg/cubeteg.png" alt="" draggable="false" />
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <p className={`${styles.hint} ${spun ? styles.hintSpent : ''}`}>
                drag to spin
            </p>
        </div>
    );
}

export default CubeTeg;
